import React, { useState } from "react";
import CustomCard from "./custom-card";
import { makeStyles } from "@material-ui/core";
import moment from "moment";
import { cardTitleStyle, cardContentStyle, cardInlineInfoStyle } from "./card-styles";
import { EditIcon } from "./icons-svg";
import CustomModal from "./custom-modal";
import store from "src/store";
import Tooltip from "./tooltip";
import OrganizationForm from "./organization-form";

const useStyles = makeStyles({
    root: {
        marginBottom: '8px',
        display: 'flex',
        boxShadow: 'none',
        padding: '20px',
        alignItems: 'center'
    },
    child: {
        flex: '1',
        padding: '5px'
    },
    rowParent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%'
    },
    content: {
        flex: '1 0 auto',
        height: '100%',
        padding: '0px !important'
    },
    rightIcon: {
        float: 'right'
    },
    staticText: {
        color: '#717178'
    },
    icon: {
        display: 'inline-block',
        fontSize: '18px',
        color: '#2E2E38',
        marginLeft: '5px'
    },
    clickable: {
        cursor: 'pointer',
    }
});

export default function OrganizationCard(props) {
    const classes = useStyles();

    const [showEditOrganizationModal, setShowEditOrganizationModal] = useState(false);

    const [name, setName] = useState('');
    const [orgAdmins, setOrgAdmins] = useState([]);
    const [plan, setPlan] = useState('');
    const [executionAllowedPerMonth, setExecutionAllowedPerMonth] = useState('');
    const [allowedEngagements, setAllowedEngagements] = useState('');
    const [allowedUsers, setAllowedUsers] = useState('');

    const [nameHasError, setNameHasError] = useState(false);
    const [nameErrorText, setNameErrorText] = useState('');

    const resetOrganizationForm = () => {
        setName('');
        setOrgAdmins([]);
        setPlan('');
        setExecutionAllowedPerMonth('');
        setAllowedEngagements('');
        setAllowedUsers('');
        setNameHasError(false);
        setNameErrorText('');
    }

    const setOrganizationData = () => {
        setName(props.organization.name);
        setOrgAdmins(props.organization.orgAdmins);
        setPlan(props.organization.plan);
        setExecutionAllowedPerMonth(props.organization.executionAllowedPerMonth);
        setAllowedEngagements(props.organization.allowedEngagements);
        setAllowedUsers(props.organization.allowedUsers);
    }

    const openEditOrganizationModal = () => {
        setOrganizationData();
        setShowEditOrganizationModal(true);
    }

    const formIsValid = () => {
        let isValid = true;

        if (!name) {
            isValid = false;
            setNameHasError(true);
            setNameErrorText("Name is required");
        } else if(store.organizations.length && store.organizations.filter((item) => (item._id != props.organization._id && item.name == name)).length ){
            isValid = false;
            setNameHasError(true);
            setNameErrorText("Name already exists");
        } else {
            setNameHasError(false);
            setNameErrorText("");
        }

        return isValid;
    }

    const updateOrganization = () => {
        if (formIsValid()) {
            let organizationData = {
                name: name,
                orgAdmins: orgAdmins,
                plan: plan,
                executionAllowedPerMonth: Number(executionAllowedPerMonth),
                allowedEngagements: Number(allowedEngagements),
                allowedUsers: Number(allowedUsers),
            };

            store.updateOrganization(organizationData, props.organization._id);
            setShowEditOrganizationModal(false);
            resetOrganizationForm();
        }
    }

    return (
        <>
            <CustomModal
                size="medium"
                title="Edit organization"
                showModal={showEditOrganizationModal}
                onCloseModal={() => { setShowEditOrganizationModal(false) }}
                submitBtnText={'Update'}
                onSubmit={() => { updateOrganization() }}
                submitBtnDisabled={false}
            >
                <OrganizationForm
                    name={name}
                    orgAdmins={orgAdmins}
                    plan={plan}
                    executionAllowedPerMonth={executionAllowedPerMonth}
                    allowedEngagements={allowedEngagements}
                    allowedUsers={allowedUsers}
                    setName={(value) => setName(value)}
                    setOrgAdmins={(value) => setOrgAdmins(value)}
                    setPlan={(value) => setPlan(value)}
                    setExecutionAllowedPerMonth={(value) => setExecutionAllowedPerMonth(value)}
                    setAllowedEngagements={(value) => setAllowedEngagements(value)}
                    setAllowedUsers={(value) => setAllowedUsers(value)}
                    nameHasError={nameHasError}
                    nameErrorText={nameErrorText}
                    idEdit={true}
                />
            </CustomModal>

            <CustomCard className={classes.root}>
                <div css={`flex-grow:1;`}>
                    <div className={classes.rowParent}>
                        <div>
                            <div
                                css={`
                                    color:inherit;
                                    text-decoration:inherit;
                                    font-size:18px;
                                `}
                            >
                                <div>
                                    <span className={classes.rightIcon}>
                                        <span className={`${classes.icon} ${classes.clickable}`} onClick={() => { openEditOrganizationModal() }}><Tooltip tip={"Edit organization"}>{EditIcon()}</Tooltip></span>
                                    </span>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Name:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {props.organization.name}
                                    </div>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Organization Admins:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {props.organization.orgAdmins?.join?.(', ') || "No one"}
                                    </div>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Plan:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {props.organization.plan || "NA"}
                                    </div>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Execution allowed per month:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {props.organization.executionAllowedPerMonth}
                                    </div>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Allowed engagements:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {props.organization.allowedEngagements}
                                    </div>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Allowed users:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {props.organization.allowedUsers}
                                    </div>
                                </div>

                                <div css={cardInlineInfoStyle}>
                                    <div css={cardTitleStyle}>
                                        Created:
                                    </div>
                                    <div css={cardContentStyle}>
                                        {moment(props.organization.created_on).format("MM-DD-YYYY | hh:mm A")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CustomCard>
        </>
    )

}